import { api } from '../api';
import { sessionStore } from './sessionStore';
import type { SessionUser } from './sessionStore';
import { describeThisDevice, forgetDevice, readDeviceToken, rememberDevice } from './deviceMemory';

/**
 * РЕАЛЬНАЯ форма ответа auth.login (проверена вживую против боевого шлюза):
 * data = { token, expiresAt, user: { id, login, profile: { name }, roles[], capabilities[] } }.
 * Токен — в data.token (НЕ sessionToken).
 *
 * С миграции 0058 ответ дополнительно несёт deviceToken/deviceExpiresAt — но
 * ТОЛЬКО когда пользователь попросил запомнить устройство.
 */
export interface AuthLoginData {
  token: string;
  expiresAt?: string;
  user: SessionUser;
  deviceToken?: string;
  deviceExpiresAt?: string;
}

/** Вход: auth.login через command-шлюз (kind=command ставит сам клиент). */
export async function login(
  loginName: string,
  password: string,
  rememberThisDevice = false,
): Promise<void> {
  const { data } = await api.command<AuthLoginData>('auth.login', {
    login: loginName,
    password,
    ...(rememberThisDevice
      ? { remember_device: true, device_label: describeThisDevice() }
      : {}),
  });
  applyAuthData(data, rememberThisDevice);
}

/**
 * Продолжение сессии на запомненном устройстве. Вызывается ОДИН раз при старте
 * приложения, до отрисовки. Токен устройства одноразовый: сервер отзывает его и
 * возвращает следующий, поэтому ответ обязательно надо сохранить.
 *
 * Любой отказ означает одно и то же с точки зрения пользователя — нужен вход
 * паролем, — поэтому забываем устройство и молча показываем экран входа.
 */
export async function resumeOnThisDevice(): Promise<boolean> {
  const deviceToken = readDeviceToken();
  if (!deviceToken) return false;
  try {
    const { data } = await api.command<AuthLoginData>('auth.resume', {
      device_token: deviceToken,
    });
    applyAuthData(data, true);
    return true;
  } catch {
    forgetDevice();
    return false;
  }
}

function applyAuthData(data: AuthLoginData, remembering: boolean): void {
  if (remembering && data.deviceToken) {
    rememberDevice(data.deviceToken, data.deviceExpiresAt ?? null);
  }
  sessionStore.login({
    token: data.token,
    user: data.user ?? null,
    expiresAt: data.expiresAt ?? null,
  });
}

/**
 * Выход: auth.logout (bearer — в заголовке Authorization, подставляет клиент).
 * Локальный стор чистим всегда, даже если сеть/шлюз ответили ошибкой.
 *
 * Явный выход ЗАБЫВАЕТ устройство и на сервере: иначе «выйти» на чужом
 * компьютере не выйдет — следующее открытие вкладки снова пустило бы внутрь.
 */
export async function logout(): Promise<void> {
  const deviceToken = readDeviceToken();
  try {
    if (deviceToken) {
      // Отзываем ИМЕННО ЭТО устройство, пока сессия ещё жива. Не `all`:
      // «выйти» на рабочем компьютере не должно разлогинивать телефон.
      await api.command('auth.device_revoke', { device_token: deviceToken })
        .catch(() => undefined);
    }
    await api.command('auth.logout');
  } catch {
    // серверный отзыв мог не пройти — сессию всё равно сбрасываем локально
  } finally {
    forgetDevice();
    sessionStore.clear();
  }
}
